import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Settings2, GripVertical, Eye, EyeOff, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getBooks } from '../api/books';
import api from '../api/client';
import AddBookModal from '../components/books/AddBookModal';

const COVERS = ['#0f2044','#042d1e','#2d1d06','#1a0d2e','#0a1f2e','#2d0a0a'];
const CCOLORS = ['#60a5fa','#34d399','#fbbf24','#a78bfa','#38bdf8','#f87171'];

const MAT_TYPES: any = {
  book:'წიგნი', periodical:'პერიოდიკა', dissertation:'დისერტაცია',
  dissertation_abstract:'სად.მაცნე', cd:'CD', proceedings:'შრომები', xerox:'ქსეროასლი'
};

const ALL_COLUMNS = [
  { key: 'id',                  label: '#',               width: 60,  always: true },
  { key: 'title',               label: 'სათაური',         width: 260, always: true, sortAsc: 'title',     sortDesc: 'title_desc' },
  { key: 'title_remainder',     label: 'ქვესათაური',      width: 180 },
  { key: 'author',              label: 'ავტორი',          width: 160, sortAsc: 'author',    sortDesc: 'author_desc' },
  { key: 'editor',              label: 'რედაქტორი',       width: 140 },
  { key: 'translator',          label: 'მთარგმნელი',      width: 140 },
  { key: 'full_account',        label: 'საბუღ. ანგარიში', width: 130 },
  { key: 'account_code',        label: 'საბუღ. კოდი',     width: 100 },
  { key: 'udc',                 label: 'UDC',             width: 100, sortAsc: 'udc',       sortDesc: 'udc_desc' },
  { key: 'shelf_code',          label: 'თარო',            width: 80,  sortAsc: 'shelf_code',sortDesc: 'shelf_desc' },
  { key: 'inventory_number',    label: 'საინვ. №',        width: 100 },
  { key: 'material_type',       label: 'ტიპი',            width: 100 },
  { key: 'publisher',           label: 'გამომცემელი',     width: 140 },
  { key: 'place_of_publication',label: 'გამოც.ადგ.',      width: 120 },
  { key: 'pub_year',            label: 'წელი',            width: 70,  sortAsc: 'pub_year_asc', sortDesc: 'pub_year' },
  { key: 'edition',             label: 'გამოცემა',        width: 100 },
  { key: 'pages',               label: 'გვერდები',        width: 80 },
  { key: 'isbn',                label: 'ISBN',            width: 130 },
  { key: 'language',            label: 'ენა',             width: 70 },
  { key: 'series',              label: 'სერია',           width: 120 },
  { key: 'series_number',       label: 'სერ.№',           width: 70 },
  { key: 'volume',              label: 'ტომი',            width: 70 },
  { key: 'volume_number',       label: 'ტომ.№',           width: 70 },
  { key: 'subject',             label: 'საგანი',          width: 130, sortAsc: 'subject' },
  { key: 'collection_name',     label: 'კოლექცია',        width: 100 },
  { key: 'copies',              label: 'ასლები',          width: 80,  always: true, sortAsc: 'copies' },
] as const;

type ColKey = typeof ALL_COLUMNS[number]['key'];

const DEFAULT_VISIBLE: ColKey[] = ['id','title','author','full_account','udc','shelf_code','inventory_number','material_type','pub_year','collection_name','copies'];

function loadCols(): ColKey[] {
  try {
    const s = localStorage.getItem('folios_book_cols');
    return s ? JSON.parse(s) : DEFAULT_VISIBLE;
  } catch { return DEFAULT_VISIBLE; }
}
function saveCols(cols: ColKey[]) {
  localStorage.setItem('folios_book_cols', JSON.stringify(cols));
}

// ── enrich progress ────────────────────────────────────────────────────────
interface EnrichState {
  status: string; progress: number; step?: string;
  current_title?: string; current_fields?: string;
  fuzzy_done?: number; fuzzy_total?: number;
  applied?: number; total_updates?: number;
  updated?: number; last_run?: string;
}

const LIMIT_OPTIONS = [12, 25, 50, 100];
function loadLimit(): number {
  return parseInt(localStorage.getItem('folios_books_limit') || '12') || 12;
}

export default function BooksPage() {
  const [page, setPage]         = useState(1);
  const [limit, setLimit]       = useState<number>(loadLimit);
  const [showAdd, setShowAdd]   = useState(false);
  const [showColMgr, setShowColMgr] = useState(false);
  const [collectionId, setCollectionId] = useState('');
  const [sortBy, setSortBy]     = useState('newest');
  const [availability, setAvailability] = useState('');
  const [visibleCols, setVisibleCols]   = useState<ColKey[]>(loadCols);
  const [dragCol, setDragCol]   = useState<ColKey|null>(null);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState(new URLSearchParams(location.search).get('q') || '');
  useEffect(() => { setQ(new URLSearchParams(location.search).get('q') || ''); }, [location.search]);

  // enrich progress polling
  const [enrich, setEnrich] = useState<EnrichState|null>(null);
  const [enrichStarting, setEnrichStarting] = useState(false);
  const pollTimer = React.useRef<any>(null);

  const pollEnrich = React.useCallback(async () => {
    try {
      const r = await api.get('/settings');
      const raw = r.data?.settings?.enrich_progress;
      if (raw) {
        const parsed: EnrichState = JSON.parse(raw);
        setEnrich(parsed);
        if (parsed.status === 'running') {
          pollTimer.current = setTimeout(pollEnrich, 3000);
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    pollEnrich();
    return () => clearTimeout(pollTimer.current);
  }, [pollEnrich]);

  const startEnrich = async () => {
    if (enrichStarting) return;
    setEnrichStarting(true);
    try {
      await api.post('/settings/enrich-start');
    } catch (e: any) {
      // 409 = already running — still start polling
      if (e?.response?.status !== 409) {
        setEnrichStarting(false);
        return;
      }
    }
    setEnrichStarting(false);
    clearTimeout(pollTimer.current);
    pollTimer.current = setTimeout(pollEnrich, 1500);
  };

  const { data: statsData } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.get('/stats'),
    staleTime: 60000,
  } as any);
  const totalCopies: number = (statsData as any)?.data?.physical_copies ?? 0;

  const { data: collsData } = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get('/books/collections'),
  } as any);

  const { data, isLoading } = useQuery({
    queryKey: ['books', q, collectionId, page, limit, sortBy, availability],
    queryFn: () => getBooks({ q, collection_id: collectionId, page, limit, sort: sortBy, availability }),
    keepPreviousData: true,
  } as any);

  const books    = (data as any)?.data?.books || [];
  const total    = (data as any)?.data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const toggleCol = (key: ColKey) => {
    const col = ALL_COLUMNS.find(c => c.key === key);
    if ((col as any)?.always) return;
    const next = visibleCols.includes(key) ? visibleCols.filter(k => k !== key) : [...visibleCols, key];
    setVisibleCols(next); saveCols(next);
  };

  const moveCol = (from: ColKey, to: ColKey) => {
    if (from === to) return;
    const arr = [...visibleCols];
    const fi = arr.indexOf(from), ti = arr.indexOf(to);
    if (fi < 0 || ti < 0) return;
    arr.splice(fi, 1); arr.splice(ti, 0, from);
    setVisibleCols(arr); saveCols(arr);
  };

  const activeCols = ALL_COLUMNS
    .filter(c => visibleCols.includes(c.key))
    .sort((a, b) => visibleCols.indexOf(a.key) - visibleCols.indexOf(b.key));

  const totalColWidth = activeCols.reduce((s, c) => s + c.width, 0);

  // column sort click
  const handleColSort = (col: any) => {
    if (!col.sortAsc) return;
    if (sortBy === col.sortAsc) setSortBy(col.sortDesc || col.sortAsc);
    else if (sortBy === col.sortDesc) setSortBy('newest');
    else setSortBy(col.sortAsc);
    setPage(1);
  };

  const getSortIcon = (col: any) => {
    if (!col.sortAsc) return null;
    if (sortBy === col.sortAsc)   return <ChevronUp size={11} style={{color:'var(--accent)',marginLeft:3}} />;
    if (sortBy === col.sortDesc)  return <ChevronDown size={11} style={{color:'var(--accent)',marginLeft:3}} />;
    return <ChevronUp size={11} style={{color:'var(--text-dimmed)',marginLeft:3,opacity:0.4}} />;
  };

  const getCellVal = (b: any, key: string) => {
    switch(key) {
      case 'id':          return b.id;
      case 'title':       return null;
      case 'material_type': return MAT_TYPES[b.material_type] || b.material_type || '—';
      case 'copies':      return null;
      case 'collection_name': return b.collection_name || '—';
      default: return b[key] || '—';
    }
  };

  const isRunning = enrich?.status === 'running';

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>
      <AddBookModal open={showAdd} onClose={() => setShowAdd(false)} />

      {/* header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <div>
          {isRunning ? (
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <Loader2 size={16} style={{color:'var(--warning)',animation:'spin 1s linear infinite'}} />
              <h1 style={{fontSize:20,fontWeight:600,color:'var(--warning)',margin:0}}>
                მიმდინარეობს წიგნების ანალიზი და შედარება
              </h1>
            </div>
          ) : (
            <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:0}}>{t('catalog')}</h1>
          )}
          <p style={{fontSize:12,color:'var(--text-faint)',margin:'4px 0 0',display:'flex',alignItems:'center',gap:6}}>
            {total.toLocaleString()} წიგნი
            {totalCopies > 0 && (
              <>
                <span style={{color:'var(--border)'}}>·</span>
                <span style={{color:'var(--danger)',fontWeight:700}}>{totalCopies.toLocaleString()} ასლი</span>
              </>
            )}
          </p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {/* enrichment trigger */}
          <button
            onClick={startEnrich}
            disabled={enrichStarting || isRunning}
            title="კატალოგის გამდიდრება Pluteus-იდან"
            style={{
              display:'flex',alignItems:'center',gap:6,padding:'8px 14px',
              background: isRunning ? 'var(--warning-bg)' : 'var(--bg-card)',
              border:`1px solid ${isRunning ? 'rgba(246,185,59,0.4)' : 'var(--border)'}`,
              borderRadius:8,
              color: isRunning ? 'var(--warning)' : 'var(--text-muted)',
              fontSize:12,cursor: (enrichStarting||isRunning) ? 'default' : 'pointer',
              opacity: enrichStarting ? 0.6 : 1,
              transition:'all 0.15s',
            }}>
            {enrichStarting || isRunning
              ? <Loader2 size={13} style={{animation:'spin 1s linear infinite'}}/>
              : <i className="ti ti-sparkles" style={{fontSize:14}}/>}
            {isRunning ? `${enrich?.progress ?? 0}%` : 'გამდიდრება'}
          </button>

          <button onClick={() => setShowColMgr(!showColMgr)}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',
              background:showColMgr?'var(--accent-bg)':'var(--bg-card)',
              border:`1px solid ${showColMgr?'var(--accent)':'var(--border)'}`,
              borderRadius:8,color:showColMgr?'var(--accent)':'var(--text-muted)',fontSize:12,cursor:'pointer'}}>
            <Settings2 size={14}/> სვეტები
          </button>
          <button onClick={() => setShowAdd(true)}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',
              background:'var(--accent)',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}>
            <Plus size={15}/> წიგნის დამატება
          </button>
        </div>
      </div>

      {/* enrich progress banner */}
      {enrich && (isRunning || enrich.status === 'done') && (
        <div style={{
          marginBottom:16,padding:'12px 16px',borderRadius:10,
          background: isRunning ? 'var(--warning-bg)' : 'var(--success-bg)',
          border:`1px solid ${isRunning ? 'rgba(246,185,59,0.4)' : 'rgba(52,199,89,0.4)'}`,
        }}>
          {isRunning ? (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {/* top row: label + % */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <Loader2 size={14} style={{color:'var(--warning)',flexShrink:0,animation:'spin 1s linear infinite'}} />
                  <span style={{fontSize:12,color:'var(--warning)',fontWeight:600}}>
                    {enrich.step === 'download'  ? 'გადმოწერა...' :
                     enrich.step === 'indexing'  ? 'ინდექსის აგება...' :
                     enrich.step === 'loading'   ? 'ჩატვირთვა...' :
                     enrich.step === 'matching'  ? 'შედარება...' :
                     enrich.step === 'fuzzy'     ? `ფაზი ანალიზი — ${enrich.fuzzy_done ?? 0} / ${enrich.fuzzy_total ?? '?'}` :
                     enrich.step === 'updating'  ? `განახლება — ${enrich.applied ?? 0} / ${enrich.total_updates ?? '?'}` :
                     'მიმდინარეობს...'}
                  </span>
                </div>
                <span style={{fontSize:13,color:'var(--warning)',fontWeight:700}}>{enrich.progress}%</span>
              </div>

              {/* progress bar */}
              <div style={{height:5,background:'var(--warning-bg)',borderRadius:3,overflow:'hidden',border:'1px solid rgba(246,185,59,0.2)'}}>
                <div style={{height:'100%',width:`${enrich.progress}%`,background:'var(--warning)',borderRadius:3,transition:'width 0.4s ease'}} />
              </div>

              {/* current book info */}
              {enrich.current_title && (
                <div style={{display:'flex',alignItems:'baseline',gap:8,fontSize:11,marginTop:2}}>
                  <span style={{color:'var(--text-faint)',flexShrink:0}}>ამჟამად:</span>
                  <span style={{
                    color:'var(--text-secondary)',
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
                    flex:1,maxWidth:480,
                    fontStyle:'italic',
                  }}>
                    {enrich.current_title}
                  </span>
                  {enrich.current_fields && (
                    <span style={{
                      flexShrink:0,padding:'1px 7px',borderRadius:12,
                      background:'rgba(246,185,59,0.15)',
                      color:'var(--warning)',fontSize:10,fontWeight:600,
                    }}>
                      {enrich.current_fields}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'var(--success)'}}>
              <i className="ti ti-circle-check" style={{fontSize:16}} />
              <span>ანალიზი დასრულდა — <strong>{enrich.updated?.toLocaleString()}</strong> ველი განახლდა</span>
              {enrich.last_run && (
                <span style={{color:'var(--text-faint)',marginLeft:4}}>
                  ({new Date(enrich.last_run).toLocaleString('ka-GE')})
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* column manager */}
      {showColMgr && (
        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:16}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
            <span style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>სვეტების მართვა</span>
            <div style={{display:'flex',gap:8}}>
              <button onClick={() => { setVisibleCols(DEFAULT_VISIBLE); saveCols(DEFAULT_VISIBLE); }}
                style={{padding:'4px 10px',background:'var(--bg-dark2)',border:'1px solid var(--border)',borderRadius:6,fontSize:11,color:'var(--text-muted)',cursor:'pointer'}}>
                ნაგულისხმევი
              </button>
              <button onClick={() => { const all = ALL_COLUMNS.map(c => c.key) as ColKey[]; setVisibleCols(all); saveCols(all); }}
                style={{padding:'4px 10px',background:'var(--bg-dark2)',border:'1px solid var(--border)',borderRadius:6,fontSize:11,color:'var(--text-muted)',cursor:'pointer'}}>
                ყველა
              </button>
            </div>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {ALL_COLUMNS.map(col => (
              <div key={col.key}
                draggable={!(col as any).always}
                onDragStart={() => setDragCol(col.key as ColKey)}
                onDragOver={e => e.preventDefault()}
                onDrop={() => { if(dragCol) moveCol(dragCol, col.key as ColKey); setDragCol(null); }}
                onClick={() => toggleCol(col.key as ColKey)}
                style={{display:'flex',alignItems:'center',gap:5,padding:'5px 10px',borderRadius:20,fontSize:11,
                  cursor:(col as any).always?'default':'pointer',
                  background:visibleCols.includes(col.key as ColKey)?'#1a2744':'#080c14',
                  border:`1px solid ${visibleCols.includes(col.key as ColKey)?'#1e3a6e':'#1e2d45'}`,
                  color:visibleCols.includes(col.key as ColKey)?'#60a5fa':'#475569',
                  opacity:(col as any).always?0.6:1}}>
                {!(col as any).always && <GripVertical size={10} style={{color:'var(--text-dimmed)'}}/>}
                {visibleCols.includes(col.key as ColKey) ? <Eye size={10}/> : <EyeOff size={10}/>}
                {col.label}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
        {/* filters */}
        <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div style={{position:'relative',flex:1,minWidth:200,maxWidth:360}}>
            <Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-dimmed)'}} />
            <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
              placeholder={t('search_books') + ' / ბარკოდი...'}
              style={{width:'100%',background:'var(--bg-input)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px 8px 32px',fontSize:12,color:'var(--text-secondary)',outline:'none',boxSizing:'border-box'}} />
          </div>
          <select value={collectionId} onChange={e => { setCollectionId(e.target.value); setPage(1); }}
            style={{background:'var(--bg-dark2)',border:'1px solid var(--border)',borderRadius:8,padding:'7px 10px',fontSize:12,color:'var(--text-muted)',cursor:'pointer',outline:'none'}}>
            <option value="">ყველა კოლექცია</option>
            {((collsData as any)?.data?.collections||[]).map((c: any) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select value={availability} onChange={e => { setAvailability(e.target.value); setPage(1); }}
            style={{background:'var(--bg-dark2)',border:'1px solid var(--border)',borderRadius:8,padding:'7px 10px',fontSize:12,color:'var(--text-muted)',cursor:'pointer',outline:'none'}}>
            <option value="">სტატუსი</option>
            <option value="available">ხელმისაწვდომი</option>
            <option value="unavailable">გაცემული</option>
          </select>
          <select value={limit} onChange={e => { const v = parseInt(e.target.value); setLimit(v); localStorage.setItem('folios_books_limit', String(v)); setPage(1); }}
            style={{background:'var(--bg-dark2)',border:'1px solid var(--border)',borderRadius:8,padding:'7px 10px',fontSize:12,color:'var(--text-muted)',cursor:'pointer',outline:'none'}}>
            {LIMIT_OPTIONS.map(n => <option key={n} value={n}>{n} / გვ.</option>)}
          </select>
          <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
            style={{background:'var(--bg-dark2)',border:'1px solid var(--border)',borderRadius:8,padding:'7px 10px',fontSize:12,color:'var(--text-muted)',cursor:'pointer',outline:'none'}}>
            <option value="newest">ახალი → ძველი</option>
            <option value="oldest">ძველი → ახალი</option>
            <option value="title">სათაური A→Z</option>
            <option value="author">ავტორი A→Z</option>
            <option value="udc">UDC ↑</option>
            <option value="shelf_code">თარო ↑</option>
            <option value="pub_year">წელი ↓</option>
            <option value="copies">ხელმისაწვდომი ↓</option>
          </select>
        </div>

        {/* table */}
        <div style={{overflowX:'auto',WebkitOverflowScrolling:'touch'}}>
          <table style={{width:'100%',minWidth:totalColWidth,borderCollapse:'collapse',fontSize:12,tableLayout:'fixed'}}>
            <colgroup>
              {activeCols.map(col => <col key={col.key} style={{width:col.width}} />)}
            </colgroup>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)'}}>
                {activeCols.map(col => {
                  const sortable = !!(col as any).sortAsc;
                  const isActive = sortBy === (col as any).sortAsc || sortBy === (col as any).sortDesc;
                  return (
                    <th key={col.key}
                      onClick={() => handleColSort(col)}
                      style={{
                        textAlign:'left',padding:'10px 12px',fontSize:11,fontWeight:500,
                        color: isActive ? 'var(--accent)' : 'var(--text-faint)',
                        letterSpacing:'0.03em',whiteSpace:'nowrap',
                        cursor: sortable ? 'pointer' : 'default',
                        userSelect:'none',
                        position:'sticky',top:0,
                        background:'var(--bg-card)',
                        overflow:'hidden',textOverflow:'ellipsis',
                      }}>
                      <span style={{display:'inline-flex',alignItems:'center'}}>
                        {col.label}
                        {getSortIcon(col)}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={activeCols.length} style={{textAlign:'center',padding:32,color:'var(--text-dimmed)'}}>
                  {t('loading')}
                </td></tr>
              )}
              {books.map((b: any, i: number) => {
                const ci = i % 6;
                return (
                  <tr key={b.id}
                    style={{borderBottom:'1px solid #0f1826',cursor:'pointer'}}
                    onClick={() => navigate(`/books/${b.id}${q && q.toUpperCase().startsWith('LIB-') ? '?barcode='+encodeURIComponent(q) : ''}`)}
                    onMouseEnter={e => (e.currentTarget.style.background='var(--bg-hover)')}
                    onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
                    {activeCols.map(col => {
                      if (col.key === 'title') return (
                        <td key="title" style={{padding:'8px 12px',overflow:'hidden'}}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            {b.cover_url
                              ? <img src={b.cover_url} alt={b.title} style={{width:24,height:32,borderRadius:3,objectFit:'cover',flexShrink:0}} onError={e => {(e.target as any).style.display='none'}} />
                              : <div style={{width:24,height:32,borderRadius:3,flexShrink:0,background:COVERS[ci],color:CCOLORS[ci],display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700}}>{(b.title||'?')[0]}</div>
                            }
                            <div style={{minWidth:0}}>
                              <div style={{color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.title||'—'}</div>
                              {b.title_remainder && !visibleCols.includes('title_remainder') &&
                                <div style={{fontSize:10,color:'var(--text-faint)',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.title_remainder}</div>}
                            </div>
                          </div>
                        </td>
                      );
                      if (col.key === 'copies') return (
                        <td key="copies" style={{padding:'8px 12px'}}>
                          <span style={{display:'inline-flex',padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:500,
                            background:b.available_copies>0?'#042d1e':'#2d1d06',
                            color:b.available_copies>0?'#34d399':'#fbbf24'}}>
                            {b.available_copies}/{b.total_copies}
                          </span>
                        </td>
                      );
                      if (col.key === 'id') return (
                        <td key="id" style={{padding:'8px 12px',color:'var(--text-dimmed)',fontSize:11}}>{b.id}</td>
                      );
                      return (
                        <td key={col.key} style={{padding:'8px 12px',color:'var(--text-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {getCellVal(b, col.key)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* pagination */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderTop:'1px solid var(--border)'}}>
          <span style={{fontSize:12,color:'var(--text-faint)'}}>გვ. {page} / {totalPages} ({total.toLocaleString()} წიგნი)</span>
          <div style={{display:'flex',gap:6,alignItems:'center'}}>

            {/* first page */}
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              title="პირველ გვერდზე"
              style={{
                padding:'6px 10px',borderRadius:8,fontSize:12,fontWeight:600,cursor:page===1?'not-allowed':'pointer',
                background: page===1 ? 'var(--bg-card)' : 'rgba(99,102,241,0.12)',
                border: `1px solid ${page===1 ? 'var(--border)' : 'rgba(99,102,241,0.35)'}`,
                color: page===1 ? 'var(--text-dimmed)' : '#818cf8',
                transition:'all 0.15s',
              }}>
              «
            </button>

            {/* prev */}
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                display:'flex',alignItems:'center',gap:5,
                padding:'6px 14px',borderRadius:8,fontSize:12,fontWeight:500,
                cursor: page===1 ? 'not-allowed' : 'pointer',
                background: page===1 ? 'var(--bg-card)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${page===1 ? 'var(--border)' : 'rgba(239,68,68,0.35)'}`,
                color: page===1 ? 'var(--text-dimmed)' : '#f87171',
                transition:'all 0.15s',
              }}>
              ← წინა
            </button>

            {/* page indicator */}
            <span style={{
              padding:'5px 12px',borderRadius:8,fontSize:12,fontWeight:600,
              background:'var(--accent-bg)',color:'var(--accent)',
              border:'1px solid rgba(37,99,235,0.25)',minWidth:52,textAlign:'center',
            }}>
              {page}
            </span>

            {/* next */}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{
                display:'flex',alignItems:'center',gap:5,
                padding:'6px 14px',borderRadius:8,fontSize:12,fontWeight:500,
                cursor: page>=totalPages ? 'not-allowed' : 'pointer',
                background: page>=totalPages ? 'var(--bg-card)' : 'rgba(37,99,235,0.1)',
                border: `1px solid ${page>=totalPages ? 'var(--border)' : 'rgba(37,99,235,0.35)'}`,
                color: page>=totalPages ? 'var(--text-dimmed)' : '#60a5fa',
                transition:'all 0.15s',
              }}>
              შემდეგი →
            </button>

          </div>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
